/* global runtime */
// Profile photo for the signed-in user (Min side).
// Loaded as a classic script before backend.jsx and shares its binding scope.
//
// The file goes to Firebase Storage under users/{uid}/profilePhoto/, and its
// download URL is saved as the Firebase Auth `photoURL`. The Firestore users
// document is deliberately not touched: its rules only accept a fixed set of
// profile fields, and the Auth profile is where a user's picture belongs.
// Exposed as window.PlummeProfilePhoto, a scoped service; UI never talks to
// Storage or Auth directly.
const PROFILE_PHOTO_MAX_BYTES = 2 * 1024 * 1024;
const PROFILE_PHOTO_MAX_EDGE = 512;
const PROFILE_PHOTO_FOLDER = 'profilePhoto';
const PROFILE_PHOTO_MIME_BY_EXTENSION = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp' };
const PROFILE_PHOTO_EXTENSION_BY_MIME = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp' };

// Errors carry a stable code so the page can show its own translated text.
const profilePhotoError = (code, message) => Object.assign(new Error(message || code), { code });

const normalizeProfilePhotoFile = (file) => {
  if (!file || typeof file !== 'object' || typeof file.size !== 'number') return { ok: false, code: 'profile-photo/missing' };
  const extension = String(file.name || '').toLowerCase().split('.').pop() || '';
  const rawType = String(file.type || '').trim().toLowerCase();
  const contentType = rawType === 'image/x-png' || rawType === 'image/jpg'
    ? (rawType === 'image/jpg' ? 'image/jpeg' : 'image/png')
    : (PROFILE_PHOTO_EXTENSION_BY_MIME[rawType] ? rawType : (PROFILE_PHOTO_MIME_BY_EXTENSION[extension] || rawType));
  if (!PROFILE_PHOTO_EXTENSION_BY_MIME[contentType]) return { ok: false, code: 'profile-photo/type', contentType };
  if (!(file.size > 0)) return { ok: false, code: 'profile-photo/empty', contentType };
  if (file.size > PROFILE_PHOTO_MAX_BYTES) return { ok: false, code: 'profile-photo/too-large', contentType };
  return { ok: true, contentType };
};

const profilePhotoStoragePath = (uid, photoId, contentType) =>
  `users/${uid}/${PROFILE_PHOTO_FOLDER}/${photoId}.${PROFILE_PHOTO_EXTENSION_BY_MIME[contentType] || 'jpg'}`;

// A Storage download URL encodes the object path after /o/. Only paths inside
// the user's own profilePhoto folder are returned, so a Google account picture
// or any other external URL is never mistaken for something we may delete.
const profilePhotoPathFromUrl = (url, uid) => {
  const match = String(url || '').match(/\/o\/([^?#]+)/);
  if (!match || !uid) return '';
  let path = '';
  try { path = decodeURIComponent(match[1]); } catch (error) { return ''; }
  return path.startsWith(`users/${uid}/${PROFILE_PHOTO_FOLDER}/`) ? path : '';
};

// Best effort: an avatar never needs more than PROFILE_PHOTO_MAX_EDGE pixels.
// When the browser cannot decode or encode, the original file is used and the
// size check still applies.
const downscaleProfilePhoto = async (file, contentType) => {
  try {
    if (typeof document === 'undefined' || typeof globalThis.createImageBitmap !== 'function') return { file, contentType };
    const bitmap = await globalThis.createImageBitmap(file, { imageOrientation: 'from-image' }).catch(() => globalThis.createImageBitmap(file));
    try {
      const width = Number(bitmap?.width || 0);
      const height = Number(bitmap?.height || 0);
      if (!width || !height) return { file, contentType };
      const scale = Math.min(1, PROFILE_PHOTO_MAX_EDGE / Math.max(width, height));
      if (scale === 1 && file.size <= PROFILE_PHOTO_MAX_BYTES) return { file, contentType };
      const canvas = document.createElement('canvas');
      if (typeof canvas?.getContext !== 'function' || typeof canvas.toBlob !== 'function') return { file, contentType };
      canvas.width = Math.max(1, Math.round(width * scale));
      canvas.height = Math.max(1, Math.round(height * scale));
      const context = canvas.getContext('2d');
      if (!context) return { file, contentType };
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      // PNG keeps transparency; everything else becomes JPEG, which every
      // browser can encode (WebP encoding is missing in older Safari).
      const outputType = contentType === 'image/png' ? 'image/png' : 'image/jpeg';
      const blob = await new Promise((resolve) => { canvas.toBlob(resolve, outputType, 0.86); });
      if (!blob || !(blob.size > 0) || blob.size >= file.size) return { file, contentType };
      return { file: blob, contentType: outputType };
    } finally {
      bitmap?.close?.();
    }
  } catch (error) {
    return { file, contentType };
  }
};

const currentProfilePhotoUser = async () => {
  await initFirebase();
  const user = runtime.auth?.currentUser;
  if (!user) throw profilePhotoError('profile-photo/signed-out', t('svc.needLoginProfile'));
  return user;
};

const emitProfilePhotoChange = (photoURL) => {
  runtime.user = runtime.auth?.currentUser || runtime.user;
  if (runtime.userProfile) runtime.userProfile = { ...runtime.userProfile, photoURL: photoURL || '' };
  emitRuntime();
};

const uploadProfilePhoto = async (file) => {
  const checked = normalizeProfilePhotoFile(file);
  // A too large photo can still be accepted once it is downscaled.
  if (!checked.ok && checked.code !== 'profile-photo/too-large') throw profilePhotoError(checked.code, checked.code === 'profile-photo/missing' ? t('svc.pickImageFile') : checked.code);
  const prepared = await downscaleProfilePhoto(file, checked.contentType);
  if (!(prepared.file.size <= PROFILE_PHOTO_MAX_BYTES)) throw profilePhotoError('profile-photo/too-large');
  const user = await currentProfilePhotoUser();
  if (!runtime.storage) throw profilePhotoError('profile-photo/no-storage', t('svc.noStorage'));
  const previousPath = profilePhotoPathFromUrl(user.photoURL, user.uid);
  const photoId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `photo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const storagePath = profilePhotoStoragePath(user.uid, photoId, prepared.contentType);
  const ref = runtime.storage.ref().child(storagePath);
  await ref.put(prepared.file, {
    contentType: prepared.contentType,
    cacheControl: 'private, max-age=31536000',
    customMetadata: { source: 'plumme-profile' },
  });
  let photoURL = '';
  try {
    photoURL = await ref.getDownloadURL();
    await user.updateProfile({ photoURL });
  } catch (error) {
    // Do not leave an orphaned object behind when the profile was not updated.
    await ref.delete().catch(() => undefined);
    throw error;
  }
  if (previousPath && previousPath !== storagePath) {
    await runtime.storage.ref().child(previousPath).delete().catch(() => undefined);
  }
  emitProfilePhotoChange(photoURL);
  return { photoURL, storagePath };
};

const removeProfilePhoto = async () => {
  const user = await currentProfilePhotoUser();
  const previousPath = profilePhotoPathFromUrl(user.photoURL, user.uid);
  await user.updateProfile({ photoURL: null });
  if (previousPath && runtime.storage) {
    await runtime.storage.ref().child(previousPath).delete().catch(() => undefined);
  }
  emitProfilePhotoChange('');
  return true;
};

window.PlummeProfilePhoto = Object.freeze({
  MAX_BYTES: PROFILE_PHOTO_MAX_BYTES,
  MAX_EDGE: PROFILE_PHOTO_MAX_EDGE,
  ACCEPT: 'image/png,image/jpeg,image/webp',
  normalizeProfilePhotoFile,
  profilePhotoPathFromUrl,
  uploadProfilePhoto,
  removeProfilePhoto,
});
